import {
    Box,
    Button,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {useEffect, useMemo, useState} from "react";
import { formatarMonetárioParaNúmero, formatarValorMonetario } from "../../../../../../helpers/masks";
import DialogCadastrarValor, { IInternFormCheckout } from "../../../MIPedidos/DialogCriarPedidoVenda/CriarPedido/Checkout/FormasPagamento/DialogCadastrarValor";
import { useFormContext } from "react-hook-form";
import { IDataPublicos } from "../../../../../../types/api/geral/pesquisar-publicos";

interface IOpenModal {
    state: boolean,
    preValue: number | null
}

interface FormasPagamentoProps {
    subTotal: number
    cliente: IDataPublicos
}

export default function FormasPagamento({ subTotal, cliente }: FormasPagamentoProps) {
    
    const { setValue, watch } = useFormContext();
    const valueRecb: IInternFormCheckout[] = watch('valoresAdicionados');
    const [openModal, setOpenModal] = useState<IOpenModal>({ state: false, preValue: null });

    const normalizarValor = (valor: string | number): number => {
        if (typeof valor === 'number') return valor;
        const valorLimpo = valor.trim();
        if (valorLimpo.includes(',')) {
            return parseFloat(valorLimpo.replace('.', '').replace(',', '.'));
        }
        return parseFloat(valorLimpo);
    };

    // Modal handlers
    const handleOpenModal = (preValue: number | null = null) => setOpenModal({ state: true, preValue });
    const handleCloseModal = () => setOpenModal({ state: false, preValue: null });

    // Remove um item da lista de valores recebidos
    const handleRemoveItem = (index: number) => {
        const valores = [...watch('valoresAdicionados')];
        valores.splice(index, 1);
        setValue('valoresAdicionados', valores);
    };

    const valorSubTotal = useMemo(() =>
        valueRecb.reduce((acc, item) => {
            const valor = formatarMonetárioParaNúmero(item.valor);
            return acc + (isNaN(valor) ? 0 : valor);
        }, 0), [valueRecb]
    );

    const totalAcrescimos = useMemo(() => {
        const soma = valueRecb.reduce((acc, item) => {
            if (item.pagamento.parcelas.length > 0 && formatarMonetárioParaNúmero(item.valor) !== item.pagamento.parcelas.reduce((acc, parcela) => acc + parcela.valor, 0)) {
                const somaParcelas = item.pagamento.parcelas.reduce((soma, parcela) => {
                    const v = normalizarValor(parcela.valor);
                    return soma + (isNaN(v) ? 0 : v);
                }, 0);
                return acc + somaParcelas;
            }
            const valor = normalizarValor(item.valor);
            return acc + (isNaN(valor) ? 0 : valor);
        }, 0);
        return soma - parseFloat(valorSubTotal.toFixed(2));
    }, [valueRecb, valorSubTotal]);

    useEffect(() => {
        setValue('valor_acrescimo', totalAcrescimos);
    }, [totalAcrescimos, setValue]);


    // Estados adicionais
    const valorRestante = watch("valor_restante");
    const condicaoPagamento = watch("condicao_pagamento");
    const disabledBtn = useMemo(() => !condicaoPagamento, [condicaoPagamento]);

    return (
        <Box mt={2}>
            {
                valueRecb.length === 0 && (
                    <Box
                        display="flex"
                        alignItems="center"
                        flexDirection="column"
                        justifyContent="center"
                        borderRadius={1}
                        height={200}
                        border="1px dashed #1B5E20"
                        gap={0.5}
                    >
                        <Typography variant="subtitle2">Clique no botão abaixo para adicionar os valores</Typography>
                        <Typography variant="caption" mb={1}>(Ex: Dinheiro, PIX, Cartão de Crédito, etc..)</Typography>
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenModal()}
                            disabled={disabledBtn || valorRestante <= 0}
                        >Adicionar</Button>
                    </Box>
                )
            }
            {
                valueRecb.length > 0 && (
                    <>
                        <Box mb={1} display="flex" alignItems="center" justifyContent="flex-end">
                            <Button
                                size="small"
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenModal()}
                                disabled={valueRecb.length === 4 || disabledBtn || valorRestante <= 0}
                            >Adicionar</Button>
                        </Box>

                        <TableContainer sx={{ border: "1px solid #ccc", borderRadius: 2 }}>
                            <Table size="small" aria-label="formas pagamento table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><b>Tipo</b></TableCell>
                                        <TableCell align="right"><b>Valor</b></TableCell>
                                        <TableCell align="right"><b>Acréscimos</b></TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {valueRecb.map((item, index) => {
                                        const valorParcelas = item.pagamento.parcelas.reduce((acc: number, parcela: any) =>
                                            acc + Number(parcela.valor), 0);
                                        const acrescimos = item.pagamento.parcelas.length > 0 && formatarMonetárioParaNúmero(item.valor) !== item.pagamento.parcelas.reduce((acc, parcela) => acc + parcela.valor, 0)
                                        ? valorParcelas - Number(normalizarValor(item.valor))
                                        : 0;

                                        return (
                                            <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                <TableCell component="th" scope="row">
                                                    {item.forma_recebimento?.nome || "-"}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatarValorMonetario(normalizarValor(item.valor))}
                                                    {item.pagamento.qtdParcelas > 0 && ` (${item.pagamento.qtdParcelas}x)`}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatarValorMonetario(acrescimos)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Box display="flex" gap={0.5} justifyContent="flex-end">
                                                        <IconButton
                                                            aria-label="edit"
                                                            size="small"
                                                            disabled={item.status === "Pago"}
                                                            onClick={() => handleOpenModal(index)}
                                                        >
                                                            <EditIcon fontSize="inherit" />
                                                        </IconButton>
                                                        <IconButton
                                                            aria-label="delete"
                                                            size="small"
                                                            color="error"
                                                            disabled={item.status === "Pago"}
                                                            onClick={() => handleRemoveItem(index)}
                                                        >
                                                            <DeleteIcon fontSize="inherit" />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    <TableRow sx={{ bgcolor: "#E8E8E8", '&:last-child td, &:last-child th': { border: 0 } }}>
                                        <TableCell component="th" scope="row"><b>Valor total</b></TableCell>
                                        <TableCell align="right"><b>{formatarValorMonetario(valorSubTotal)}</b></TableCell>
                                        <TableCell align="right"><b>{formatarValorMonetario(totalAcrescimos)}</b></TableCell>
                                        <TableCell align="right"><b>{formatarValorMonetario(totalAcrescimos + valorSubTotal)}</b></TableCell>
                                    </TableRow>
                                    <TableRow sx={{ bgcolor: "#e6cfcf", '&:last-child td, &:last-child th': { border: 0 } }}>
                                        <TableCell component="th" scope="row"><b>Valor restante</b></TableCell>
                                        <TableCell align="right" colSpan={3}><b>{formatarValorMonetario(valorRestante)}</b></TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )
            }
            {openModal.state && (
                <DialogCadastrarValor
                    cliente={cliente}
                    subTotal={subTotal}
                    open={openModal.state}
                    value={openModal.preValue}
                    handleClose={handleCloseModal}
                />
            )}
        </Box>
    );
}